import { useState, useEffect } from 'react';
import { motion, PanInfo, useMotionValue, useTransform } from 'motion/react';
import { ChevronUp, MapPin, Navigation, Zap, GripHorizontal } from 'lucide-react';
import { ChargingLocation } from '../../types';
import { LocationDisplayStatus } from '../../store/locationStatusStore';

/**
 * Effective availability data for a location
 */
interface EffectiveAvailability {
  isAvailable: boolean;
  displayStatus: LocationDisplayStatus;
  availableCount: number;
  totalCount: number;
}

interface MapBottomDrawerProps {
  locations: ChargingLocation[];
  onLocationClick: (location: ChargingLocation) => void;
  hasChargingBottomNav?: boolean;
  /** Get effective availability for a location. If not provided, falls back to static data */
  getEffectiveAvailability?: (location: ChargingLocation) => EffectiveAvailability;
  /** Called when the drawer state changes (minimized/half/full) */
  onDrawerStateChange?: (state: DrawerState) => void;
}

// Status dot colors for three-state
const statusDotColors: Record<LocationDisplayStatus, string> = {
  available: 'bg-green-500',
  occupied: 'bg-amber-500',
  offline: 'bg-gray-400',
};

// Status icon background colors
const statusIconBgColors: Record<LocationDisplayStatus, string> = {
  available: 'bg-green-100 dark:bg-green-900',
  occupied: 'bg-amber-100 dark:bg-amber-900',
  offline: 'bg-gray-100 dark:bg-gray-700',
};

// Status icon text colors
const statusIconTextColors: Record<LocationDisplayStatus, string> = {
  available: 'text-green-600 dark:text-green-400',
  occupied: 'text-amber-600 dark:text-amber-400',
  offline: 'text-gray-500 dark:text-gray-400',
};

export type DrawerState = 'minimized' | 'half' | 'full';

export function MapBottomDrawer({ locations, onLocationClick, hasChargingBottomNav = false, getEffectiveAvailability, onDrawerStateChange }: MapBottomDrawerProps) {
  const [drawerState, setDrawerState] = useState<DrawerState>('minimized');
  const y = useMotionValue(0);

  // Notify parent of drawer state changes
  useEffect(() => {
    onDrawerStateChange?.(drawerState);
  }, [drawerState, onDrawerStateChange]);

  // Sort locations by distance
  const sortedLocations = [...locations].sort((a, b) => a.distance - b.distance);

  // BottomNav is always present now (in ProtectedLayout)
  // Regular BottomNav: ~64px, Charging BottomNav: ~90px
  const BOTTOM_NAV_HEIGHT = hasChargingBottomNav ? 90 : 64;

  // Heights for different states
  // Drawer is positioned above the navbar (bottom: BOTTOM_NAV_HEIGHT)
  const MINIMIZED_HEIGHT = 140;
  const HALF_HEIGHT = 380;
  const FULL_HEIGHT = Math.min(window.innerHeight * 0.8, window.innerHeight - BOTTOM_NAV_HEIGHT - 20) - BOTTOM_NAV_HEIGHT;

  const getHeight = () => {
    switch (drawerState) {
      case 'minimized':
        return MINIMIZED_HEIGHT;
      case 'half':
        return HALF_HEIGHT;
      case 'full':
        return FULL_HEIGHT;
      default:
        return MINIMIZED_HEIGHT;
    }
  };

  const handleDragEnd = (event: any, info: PanInfo) => {
    const velocity = info.velocity.y;
    const offset = info.offset.y;

    // Determine next state based on drag direction and current state
    if (velocity > 500 || offset > 50) {
      // Dragging down
      if (drawerState === 'full') {
        setDrawerState('half');
      } else if (drawerState === 'half') {
        setDrawerState('minimized');
      }
    } else if (velocity < -500 || offset < -50) {
      // Dragging up
      if (drawerState === 'minimized') {
        setDrawerState('half');
      } else if (drawerState === 'half') {
        setDrawerState('full');
      }
    }
  };

  const handleExpand = () => {
    if (drawerState === 'minimized') {
      setDrawerState('half');
    } else if (drawerState === 'half') {
      setDrawerState('full');
    }
  };

  const handleCollapse = () => {
    if (drawerState === 'full') {
      setDrawerState('half');
    } else if (drawerState === 'half') {
      setDrawerState('minimized');
    }
  };

  const getDisplayedLocations = () => {
    switch (drawerState) {
      case 'minimized':
        return sortedLocations.slice(0, 3);
      case 'half':
        return sortedLocations.slice(0, 5);
      case 'full':
        return sortedLocations;
      default:
        return sortedLocations.slice(0, 3);
    }
  };

  return (
    <motion.div
      drag="y"
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={0.1}
      onDragEnd={handleDragEnd}
      animate={{ height: getHeight() }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="fixed left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl shadow-[0_-4px_30px_rgba(0,0,0,0.15)] dark:shadow-[0_-4px_30px_rgba(0,0,0,0.5)] z-[1000] overflow-hidden"
      style={{
        touchAction: 'none',
        bottom: BOTTOM_NAV_HEIGHT
      }}
    >
      {/* Drag Handle */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 z-10 pt-3 pb-2 px-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          {/* Handle bar */}
          <div className="flex-1 flex justify-center">
            <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full cursor-grab active:cursor-grabbing" />
          </div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#8bc43e]/10 rounded-xl flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#8bc43e]" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">
                {drawerState === 'minimized' 
                  ? 'Nearest Stations' 
                  : drawerState === 'half'
                  ? 'Top 5 Stations'
                  : 'All Stations'}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {locations.length} stations found
              </p>
            </div>
          </div>

          {/* Expand/Collapse button */}
          <button
            onClick={drawerState === 'full' ? handleCollapse : handleExpand}
            className="w-9 h-9 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <motion.div
              animate={{ rotate: drawerState === 'full' ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </motion.div>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="overflow-y-auto px-4 pb-[max(1rem,env(safe-area-inset-bottom))]" style={{ height: `calc(${getHeight()}px - 100px)` }}>
        {drawerState === 'minimized' ? (
          // Minimized: Compact peek view
          <div className="space-y-2 mt-3">
            {getDisplayedLocations().map((location, index) => {
              // Get effective availability - use provided getter or fall back to static data
              const availability: EffectiveAvailability = getEffectiveAvailability
                ? getEffectiveAvailability(location)
                : {
                    isAvailable: location.available,
                    displayStatus: location.available ? 'available' : 'occupied',
                    availableCount: location.chargepoints.filter(cp => cp.status === 'available').length,
                    totalCount: location.chargepoints.length,
                  };
              const { displayStatus, availableCount, totalCount } = availability;

              return (
                <motion.button
                  key={location.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => onLocationClick(location)}
                  className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors active:scale-[0.98]"
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDotColors[displayStatus]}`} />

                  <div className="flex-1 text-left min-w-0">
                    <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                      {location.name}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <Navigation className="w-3 h-3" />
                      {Math.round(location.distance)} km
                    </span>
                    <span className={`text-xs font-medium ${statusIconTextColors[displayStatus]}`}>
                      {availableCount}/{totalCount}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        ) : (
          // Half/Full: Card view with more details
          <div className="space-y-3 mt-3">
            {getDisplayedLocations().map((location, index) => {
              // Get effective availability - use provided getter or fall back to static data
              const availability: EffectiveAvailability = getEffectiveAvailability
                ? getEffectiveAvailability(location)
                : {
                    isAvailable: location.available,
                    displayStatus: location.available ? 'available' : 'occupied',
                    availableCount: location.chargepoints.filter(cp => cp.status === 'available').length,
                    totalCount: location.chargepoints.length,
                  };
              const { displayStatus, availableCount, totalCount } = availability;

              return (
                <motion.button
                  key={location.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => onLocationClick(location)}
                  className="w-full bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all active:scale-[0.98] text-left border border-gray-100 dark:border-gray-600"
                >
                  <div className="flex items-start gap-3">
                    {/* Status indicator */}
                    <div className={`mt-1 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${statusIconBgColors[displayStatus]}`}>
                      <MapPin className={`w-6 h-6 ${statusIconTextColors[displayStatus]}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="font-bold text-gray-900 dark:text-white">
                          {location.name}
                        </h4>
                        <div className="flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg px-2 py-1 flex-shrink-0">
                          <Navigation className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {Math.round(location.distance)} km
                          </span>
                        </div>
                      </div>

                      {/* Address */}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-1">
                        {location.address}
                      </p>

                      {/* Stats */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <Zap className={`w-4 h-4 ${statusIconTextColors[displayStatus]}`} />
                          <span className={`text-sm font-medium ${statusIconTextColors[displayStatus]}`}>
                            {displayStatus === 'offline'
                              ? 'Offline'
                              : `${availableCount}/${totalCount} available`}
                          </span>
                        </div>

                        {/* Connector types */}
                        <div className="flex items-center gap-1 flex-wrap">
                          {[...new Set(location.chargepoints.map(cp => cp.type))].slice(0, 2).map((type) => (
                            <span
                              key={type}
                              className="text-xs bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-md"
                            >
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      {/* State indicator dots */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
        <div className={`w-1.5 h-1.5 rounded-full transition-all ${
          drawerState === 'minimized' 
            ? 'bg-[#8bc43e] w-4' 
            : 'bg-gray-300 dark:bg-gray-600'
        }`} />
        <div className={`w-1.5 h-1.5 rounded-full transition-all ${
          drawerState === 'half' 
            ? 'bg-[#8bc43e] w-4' 
            : 'bg-gray-300 dark:bg-gray-600'
        }`} />
        <div className={`w-1.5 h-1.5 rounded-full transition-all ${
          drawerState === 'full' 
            ? 'bg-[#8bc43e] w-4' 
            : 'bg-gray-300 dark:bg-gray-600'
        }`} />
      </div>
    </motion.div>
  );
}